import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import {
  VictoryAxis, VictoryBar, VictoryChart, VictoryLabel, VictoryTooltip,
} from 'victory';
import APP_FONT from '../../helpers/font';

// Billable loans per month ({ month: 'YYYY-MM', loans }); the selected months
// are dark. Click a bar to select that month.

const AXIS = {
  axis: { stroke: '#D7DEEA' },
  tickLabels: {
    fontSize: 11, fill: '#8A94A6', padding: 6, fontFamily: APP_FONT,
  },
  grid: { stroke: 'transparent' },
};
const Y_AXIS = { ...AXIS, axis: { stroke: 'transparent' }, grid: { stroke: '#EEF1F6' } };
const PADDING = {
  top: 22, bottom: 34, left: 44, right: 12,
};
const COLOR = '#9DB4F0';
const SELECTED = '#2E3B6E';

const useWidth = () => {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => setWidth(el.clientWidth);
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
};

export default function MonthlyBars({ months = [], selected = [], onSelect }) {
  const [ref, width] = useWidth();
  const data = months.map((m, i) => ({
    x: i + 1, y: m.loans, month: m.month, tick: moment(m.month, 'YYYY-MM').format('MMM YY'),
  }));
  const max = Math.max(0, ...months.map((m) => m.loans));
  const click = [{
    target: 'data',
    eventHandlers: {
      onClick: () => [{ target: 'data', mutation: ({ datum }) => { onSelect(datum.month); return null; } }],
    },
  }];

  return (
    <div ref={ref} className='br-chart'>
      {width ? (
        <VictoryChart
          responsive={false}
          height={230}
          width={width}
          padding={PADDING}
          domainPadding={{ x: 18 }}
          domain={{ y: [0, Math.max(4, Math.ceil(max * 1.15))] }}
        >
          <VictoryAxis style={AXIS} tickValues={data.map((d) => d.x)} tickFormat={(t) => (data[t - 1] ? data[t - 1].tick : '')} />
          <VictoryAxis dependentAxis style={Y_AXIS} tickFormat={(t) => (Number.isInteger(t) ? t.toLocaleString('en-IN') : '')} />
          <VictoryBar
            data={data}
            barRatio={0.65}
            cornerRadius={{ top: 3 }}
            style={{ data: { fill: ({ datum }) => (selected.includes(datum.month) ? SELECTED : COLOR), cursor: 'pointer' } }}
            labels={({ datum }) => (datum.y ? datum.y.toLocaleString('en-IN') : '')}
            labelComponent={(
              <VictoryLabel
                dy={-3}
                style={{
                  fontSize: 10, fontWeight: 600, fill: '#5B6472', fontFamily: APP_FONT,
                }}
              />
            )}
            events={click}
          />
          {/* Transparent copy on top: hover tooltip, and the click target. */}
          <VictoryBar
            data={data}
            barRatio={0.65}
            style={{ data: { fill: 'transparent', cursor: 'pointer' } }}
            labels={({ datum }) => [
              `${datum.y.toLocaleString('en-IN')} billable ${datum.y === 1 ? 'loan' : 'loans'}`,
              moment(datum.month, 'YYYY-MM').format('MMMM YYYY'),
            ]}
            labelComponent={(
              <VictoryTooltip
                constrainToVisibleArea
                cornerRadius={8}
                pointerLength={6}
                pointerWidth={12}
                flyoutPadding={{
                  top: 7, bottom: 7, left: 11, right: 11,
                }}
                flyoutStyle={{ fill: '#1F2733', stroke: 'none' }}
                style={[
                  {
                    fill: '#FFFFFF', fontSize: 13, fontWeight: 600, fontFamily: APP_FONT,
                  },
                  { fill: '#C9D1E1', fontSize: 11, fontFamily: APP_FONT },
                ]}
              />
            )}
            events={click}
          />
        </VictoryChart>
      ) : null}
    </div>
  );
}
